import { MeasurementError } from '../app/errors';
import { log } from '../app/logger';

/**
 * One run at a time, process-wide.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * The first real-device session ran two thermal tests whose lifetimes overlapped: the second run's
 * heat workers started while the first was still sampling. The first run therefore measured under
 * **eight** CPU-saturating workers on a four-core device — double its designed load — and reported
 * a commit p99 of 111 ms with 2.5 % of taps over budget, which published `f9.outcome: "breached"`.
 * That was a **false product finding**: it escalated a stakeholder decision about what
 * acknowledgment means, on data the clean run does not support. The clean thermal run, carrying
 * only its own four workers, passed everything — commit p99 5 ms, zero of 200 over budget.
 *
 * `runner.ts` single-flights *trials*. Nothing single-flighted *runs*. This does.
 *
 * ── What the lock must cover ────────────────────────────────────────────────
 * The whole run lifetime, **including the soak** — not just the sampling window. The contamination
 * came from heat workers spawned during a second run's soak while the first was still sampling, so
 * a lock that only spanned sampling would not have prevented it. `runSession` therefore acquires
 * before `ThermalLoad.start()` and releases after `ThermalLoad.stop()`, and the live-tap pad holds
 * a lease for as long as it is on screen.
 *
 * ── Why refusing is the right behavior ──────────────────────────────────────
 * A measurement instrument that can silently invalidate itself is worse than one that refuses to
 * start. This has to hold at a real match, where the operator is coaching a team and will not
 * notice that two runs are interleaved — the failure is invisible at the moment it happens and only
 * recoverable by reconstructing timestamps afterwards.
 */

const MODULE = 'runLock';

export interface ActiveRun {
  runId: string;
  /** Human-readable, so the refusal message names what is already running. */
  label: string;
  startedAtMs: number;
}

let active: ActiveRun | null = null;
const listeners = new Set<() => void>();

/**
 * Notified whenever the lock is taken or released.
 *
 * The service-worker update prompt uses this. Applying an update reloads the page, which would
 * destroy a run in progress, so the prompt is withheld while the lock is held — and it has to
 * appear the moment the lock releases, without polling. Returns an unsubscribe function.
 */
export function subscribeRunLock(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  // A throwing listener must not corrupt lock state or stop the others from being told.
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch (cause) {
      log.exception(MODULE, 'a run-lock listener threw; lock state is unaffected', cause);
    }
  }
}

/** The run currently holding the lock, or `null`. Read-only view for the UI. */
export function activeRun(): ActiveRun | null {
  return active;
}

export function isRunActive(): boolean {
  return active !== null;
}

/**
 * Claims the lock for the whole lifetime of a run.
 *
 * @returns a release function. It is idempotent and only releases the lease it created, so a late
 *   release from an abandoned run cannot free a lock a *different* run now holds.
 * @throws {MeasurementError} when another run holds it. Refusing is the point: the alternative is
 *   two runs contaminating each other in silence.
 */
export function acquireRunLock(runId: string, label: string, nowMs: number): () => void {
  if (active !== null) {
    throw new MeasurementError(
      `A ${active.label} run is already in progress and must finish before another can start. ` +
        'Overlapping runs contaminate each other — the earlier one would measure under this one’s load and report a latency the device never actually produced.',
      { context: { blockedBy: active.runId, blockedByLabel: active.label, attempted: runId } },
    );
  }

  const lease: ActiveRun = { runId, label, startedAtMs: nowMs };
  active = lease;
  log.info(MODULE, 'run lock acquired', { runId, label });
  notify();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    // Only clear the lock if it is still ours. A stale release must never free someone else's.
    if (active === lease) {
      active = null;
      log.info(MODULE, 'run lock released', { runId, label });
      notify();
    } else {
      log.warn(MODULE, 'stale run-lock release ignored', { runId, heldBy: active?.runId ?? null });
    }
  };
}

/**
 * Test seam. Deliberately NOT exported for production use — a "force unlock" control would be
 * reached for at a venue the first time a lease leaked, and would reintroduce exactly the
 * contamination this module prevents.
 */
export function __resetRunLockForTests(): void {
  active = null;
  listeners.clear();
}
